import { InputMaybe, Order_By } from "../hasura/generated/graphql"
import { AuthService } from "@/services/AuthService"

export type ColumnsOrderBy = {
  [key: string]: InputMaybe<Order_By>
}

export type GraphQLRequest = RequestInit & {
  query?: string
  variables?: Record<string, unknown>
  operationName?: string
}

/**
 * Modifies the given `orderBy` object to sort `null` values first in ascending order and last in descending order.
 *
 * Hasura order-by types nest for related tables — ordering `install_codes` by
 * its related meter is `{ meter: { msn: 'asc' } }` rather than a plain column
 * direction. Only string values are column directions, so anything else is
 * copied through untouched; rewriting it would both mistype the result and
 * throw, since an object has no `startsWith`.
 *
 * Generic in the caller's order-by type so a table-specific type (e.g.
 * `Install_Codes_Order_By`) survives the round trip instead of being widened to
 * the flat `ColumnsOrderBy`.
 *
 * @param orderBy - The original order-by object specifying the sorting order.
 * @returns A copy with `AscNullsFirst` or `DescNullsLast` sorting order.
 */
export function nullsFirstInOrder<T extends object>(orderBy: T): T {
  const newOrderBy = Object.fromEntries(
    Object.entries(orderBy).map(([key, value]) => {
      // Nested relationship order-bys and nulls are carried through as-is.
      if (typeof value !== "string") return [key, value]
      if (value.startsWith("asc")) return [key, Order_By.AscNullsFirst]
      if (value.startsWith("desc")) return [key, Order_By.DescNullsLast]
      return [key, value]
    }),
  )

  // The rewrite only ever replaces a column direction with another direction,
  // so the shape is unchanged; TypeScript cannot see that through the generic.
  return newOrderBy as T
}

/**
 * Flips the order of specified keys in the given `orderBy` object.
 * If the key's order is 'asc', it will be changed to 'desc', and vice versa.
 * If the key's order is not specified or is neither 'asc' nor 'desc', it will remain unchanged.
 *
 * @param orderBy - The original `ColumnsOrderBy` object.
 * @param keys - The keys whose order needs to be flipped. Can be a single key or an array of keys.
 * @returns A new `ColumnsOrderBy` object with the flipped order of specified keys.
 */
export function flipOrder(
  orderBy: ColumnsOrderBy,
  keys: string[] | string,
): ColumnsOrderBy {
  const newOrderBy: ColumnsOrderBy = {}
  if (typeof keys === "string") keys = [keys]

  for (const key of keys) {
    const order: InputMaybe<Order_By> = orderBy[key]
    if (order?.startsWith("asc")) {
      newOrderBy[key] = order.replace("asc", "desc") as Order_By
    } else if (order?.startsWith("desc")) {
      newOrderBy[key] = order.replace("desc", "asc") as Order_By
    } else {
      newOrderBy[key] = order
    }
  }

  return { ...orderBy, ...newOrderBy }
}

/**
 * Converts an array to a string representation for use in Hasura custom functions when the type is _text.
 *
 * @param arr - The array to be formatted.
 * @returns A string representation of the array or null if the array is null or empty.
 */
export function formatArray(arr: string[] | null | undefined): string | null {
  return arr?.length ? `{${arr.join(", ")}}` : null
}

/**
 * Adds an Authorization header with a bearer token to the given request object.
 *
 * @template T - A subtype of GraphQLRequest that may include additional fields.
 * @param {T} request - The original request object to enhance with authentication.
 * @returns {Promise<T>} A promise that resolves to the updated request with the Authorization header.
 */
export async function addAuthToRequest<T extends GraphQLRequest>(
  request: T,
): Promise<T> {
  const { idToken } = await AuthService.getToken()

  return {
    ...request,
    headers: {
      ...request.headers,
      Authorization: `Bearer ${idToken}`,
    },
  }
}
